import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { Contract, AnalysisState } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

interface RevealPathProps {
  activeContract: Contract | null;
  analysisState: AnalysisState;
  /** 从历史记录点进已完成合同并展示结果时为 true */
  viewingHistoryResult?: boolean;
  onDownload?: () => void;
  onFileUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

// 风险分类类型
type RiskCategory = 'critical' | 'high' | 'medium' | 'low';

interface CategoryCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export const RevealPath: React.FC<RevealPathProps> = ({
  activeContract,
  analysisState,
  viewingHistoryResult = false,
  onDownload,
  onFileUpload
}) => {
  const { t, lang } = useLanguage();
  const isHistoryModeInit = !!viewingHistoryResult && !!activeContract && activeContract.status === 'completed' && !analysisState.isAnalyzing;
  const [displayedThoughtChain, setDisplayedThoughtChain] = useState<string>('');
  const [animatedCounts, setAnimatedCounts] = useState<CategoryCounts>({ critical: 0, high: 0, medium: 0, low: 0 });
  const [animatedProgress, setAnimatedProgress] = useState<number>(isHistoryModeInit ? 100 : 0);
  const [showChatGPTDialog, setShowChatGPTDialog] = useState<boolean>(isHistoryModeInit);
  const [isTypingComplete, setIsTypingComplete] = useState<boolean>(isHistoryModeInit);
  const [isFadingOut, setIsFadingOut] = useState<boolean>(false);
  const [isFadingIn, setIsFadingIn] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<'progress' | 'chat'>(isHistoryModeInit ? 'chat' : 'progress');
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string; isTyping?: boolean }>>([]);
  const [chatInput, setChatInput] = useState<string>('');
  const [isChatLoading, setIsChatLoading] = useState<boolean>(false);
  const [simulatedStepIndex, setSimulatedStepIndex] = useState<number>(0);
  const [simulatedStepsComplete, setSimulatedStepsComplete] = useState<boolean>(isHistoryModeInit);
  const thoughtChainScrollRef = useRef<HTMLDivElement | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const thoughtChainRef = useRef<string>('');
  const progressAnimationRef = useRef<NodeJS.Timeout | null>(null);
  const hasTypedOnceRef = useRef<boolean>(false);
  const previousContractIdRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevCountsRef = useRef<CategoryCounts>({ critical: 0, high: 0, medium: 0, low: 0 });

  // 计算四个风险分类的数量
  const categoryCounts = useMemo<CategoryCounts>(() => {
    if (!activeContract?.clauses) {
      return { critical: 0, high: 0, medium: 0, low: 0 };
    }
    const clauses = activeContract.clauses;
    return {
      critical: clauses.filter(c => c.riskLevel === 'CRITICAL').length,
      high: clauses.filter(c => c.riskLevel === 'HIGH').length,
      medium: clauses.filter(c => c.riskLevel === 'MEDIUM').length,
      low: clauses.filter(c => c.riskLevel === 'LOW').length
    };
  }, [activeContract?.clauses]);

  // 平滑更新数字动画
  const suppressLoadingUI = !!viewingHistoryResult || (!!activeContract && activeContract.status === 'completed' && !analysisState.isAnalyzing);
  useEffect(() => {
    if (suppressLoadingUI) {
      setAnimatedCounts({ ...categoryCounts });
      return;
    }
    
    // 使用单个 setInterval 来更新所有数字
    const interval = setInterval(() => {
      setAnimatedCounts(prev => {
        let hasChanges = false;
        const newCounts: CategoryCounts = { ...prev };
        
        Object.keys(categoryCounts).forEach(key => {
          const target = categoryCounts[key as RiskCategory];
          const current = prev[key as RiskCategory];
          if (current !== target) {
            hasChanges = true;
            const diff = target - current;
            const step = Math.max(1, Math.ceil(Math.abs(diff) / 15));
            const direction = diff > 0 ? 1 : -1;
            const newValue = current + (step * direction);
            if ((direction > 0 && newValue >= target) || (direction < 0 && newValue <= target)) {
              newCounts[key as RiskCategory] = target;
            } else {
              newCounts[key as RiskCategory] = newValue;
            }
          }
        });
        
        return hasChanges ? newCounts : prev;
      });
    }, 30);
    
    return () => clearInterval(interval);
  }, [categoryCounts, suppressLoadingUI]);

  // 模拟进度提示
  useEffect(() => {
    if (suppressLoadingUI || !analysisState.isAnalyzing || simulatedStepsComplete) return;
    if (analysisState.progress > 0) {
      setSimulatedStepsComplete(true);
      return;
    }
    const n = 4;
    const tms = setInterval(() => setSimulatedStepIndex(i => (i + 1) % n), 600);
    return () => clearInterval(tms);
  }, [suppressLoadingUI, analysisState.isAnalyzing, analysisState.progress, simulatedStepsComplete]);

  // 平滑进度条动画 - 开始分析时就开始走，但走满要等到打字机完成
  const skipProgressAnimation = useMemo(() => 
    !!viewingHistoryResult || (!!activeContract && activeContract.status === 'completed' && !analysisState.isAnalyzing),
    [viewingHistoryResult, activeContract?.status, analysisState.isAnalyzing]
  );
  useLayoutEffect(() => {
    if (progressAnimationRef.current) {
      clearInterval(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }
    if (skipProgressAnimation) {
      setAnimatedProgress(100);
      return;
    }
    // 如果正在打字，不更新进度条（由打字机控制最后10%）
    if (typingIntervalRef.current) return;
    
    const targetProgress = analysisState.progress || 0;
    
    // 如果分析完成但打字机还没开始，进度条应该到90%（打字机负责最后10%）
    if (!analysisState.isAnalyzing && targetProgress >= 100 && !hasTypedOnceRef.current) {
      setAnimatedProgress(90);
      return;
    }
    
    // 如果分析完成且打字机也完成了，进度条到100%
    if (!analysisState.isAnalyzing && targetProgress >= 100 && hasTypedOnceRef.current) {
      setAnimatedProgress(100);
      return;
    }
    
    // 如果正在分析，进度条跟随分析进度（最多到90%，打字机负责最后10%）
    if (analysisState.isAnalyzing) {
      const maxProgress = Math.min(targetProgress, 90);
      const animateProgress = () => {
        setAnimatedProgress(prev => {
          if (prev >= maxProgress) {
            if (progressAnimationRef.current) clearInterval(progressAnimationRef.current);
            return maxProgress;
          }
          const diff = maxProgress - prev;
          const step = diff > 10 ? 0.5 : (diff > 1 ? 0.1 : 0.05);
          return Math.min(prev + step, maxProgress);
        });
      };
      progressAnimationRef.current = setInterval(animateProgress, 30);
      return () => { if (progressAnimationRef.current) clearInterval(progressAnimationRef.current); };
    }
  }, [analysisState.progress, analysisState.isAnalyzing, activeContract?.status, simulatedStepsComplete, skipProgressAnimation]);

  // 打字机效果显示思维链
  useEffect(() => {
    if (viewingHistoryResult || hasTypedOnceRef.current) return;
    if (!analysisState.isAnalyzing && analysisState.progress >= 100 && activeContract?.status === 'completed') {
      if (activeContract.clauses && activeContract.clauses.length > 0) {
        const allThoughts = activeContract.clauses
          .map((c, idx) => {
            const header = lang === 'zh' ? `【条款 ${idx + 1} 分析思维链】` : `【Clause ${idx + 1} Analysis Thought Chain】`;
            return `${header}\n${c.thoughtChain || ''}`;
          })
          .filter(Boolean)
          .join('\n\n');
        
        if (allThoughts && allThoughts !== thoughtChainRef.current) {
          thoughtChainRef.current = allThoughts;
          previousContractIdRef.current = activeContract.id ?? null;
            startTypingEffect(allThoughts);
        }
      }
    }
  }, [analysisState.isAnalyzing, analysisState.progress, activeContract?.clauses, activeContract?.status, activeContract?.id, lang, viewingHistoryResult]);

  const startTypingEffect = (text: string) => {
    if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
      setDisplayedThoughtChain('');
    thoughtChainRef.current = text;
    let currentIndex = 0;
    const totalLength = text.length;
    const baseProgress = 90;
    const remainingProgress = 10;

    typingIntervalRef.current = setInterval(() => {
      if (currentIndex < text.length) {
        setDisplayedThoughtChain(text.substring(0, currentIndex + 1));
        const typingProgress = ((currentIndex + 1) / totalLength) * remainingProgress;
        setAnimatedProgress(Math.min(baseProgress + typingProgress, 100));
        currentIndex++;
      } else {
        setAnimatedProgress(100);
        setIsTypingComplete(true);
        hasTypedOnceRef.current = true;
        if (typingIntervalRef.current) clearInterval(typingIntervalRef.current);
        // 打字完成后，不自动跳转，等待用户点击"Go to Chat"按钮
      }
    }, 5);
  };

  // 辅助滚动 - 自动滚动到最新内容（仅在打字进行中且容器可见时）
  useEffect(() => {
    const el = thoughtChainScrollRef.current;
    if (el && !isTypingComplete && !isFadingOut && currentPage === 'progress') {
      // 使用 requestAnimationFrame 确保 DOM 更新后再滚动
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [displayedThoughtChain, isTypingComplete, isFadingOut, currentPage]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chatMessages, isChatLoading]);

  // 移除自动跳转对话框的逻辑，用户需要手动点击进入对话

  // 构建对话前置知识：合同内容、分析结果、修改建议、思维链、相关法律法规
  const buildChatContext = (): string => {
    if (!activeContract) return '';
    const c = activeContract;
    const zh = lang === 'zh';
    const sections: string[] = [];

    sections.push(zh ? `【合同名称】\n${c.name}` : `【Contract】\n${c.name}`);

    if (c.fullContent && c.fullContent.length > 0) {
      sections.push(zh ? `【合同正文】\n${c.fullContent.join('\n')}` : `【Contract Text】\n${c.fullContent.join('\n')}`);
    } else if (c.clauses && c.clauses.length > 0) {
      const text = c.clauses.map(cl => cl.originalText).filter(Boolean).join('\n\n');
      if (text) sections.push(zh ? `【合同正文】\n${text}` : `【Contract Text】\n${text}`);
    }

    if (c.summary) {
      sections.push(zh ? `【风险摘要】\n${c.summary}` : `【Risk Summary】\n${c.summary}`);
    }

    if (c.clauses && c.clauses.length > 0) {
      const clauseBlocks = c.clauses.map((cl, i) => {
        const p = [
          zh ? `条款 ${i + 1}` : `Clause ${i + 1}`,
          cl.originalText,
          zh ? `风险等级: ${cl.riskLevel}` : `Risk: ${cl.riskLevel}`,
          cl.riskDescription,
          zh ? `修订建议: ${cl.suggestedAction}` : `Suggestion: ${cl.suggestedAction}`,
          cl.revisedText ? (zh ? `修订文本: ${cl.revisedText}` : `Revised: ${cl.revisedText}`) : '',
          cl.thoughtChain ? (zh ? `思维链:\n${cl.thoughtChain}` : `Thought chain:\n${cl.thoughtChain}`) : '',
          (cl.legalReferences && cl.legalReferences.length) ? (zh ? `法律引用: ${cl.legalReferences.join('; ')}` : `Legal refs: ${cl.legalReferences.join('; ')}`) : ''
        ].filter(Boolean);
        return p.join('\n');
      });
      sections.push(zh ? `【条款分析、修改建议与思维链】\n${clauseBlocks.join('\n\n---\n\n')}` : `【Clause Analysis, Suggestions & Thought Chain】\n${clauseBlocks.join('\n\n---\n\n')}`);
    }

    if (c.legalReferences && c.legalReferences.length > 0) {
      const refs = c.legalReferences.map(r => `${r.title} ${r.article || ''}: ${r.content}`).join('\n');
      sections.push(zh ? `【相关法律法规】\n${refs}` : `【Related Laws & Regulations】\n${refs}`);
    }

    return sections.join('\n\n');
  };

  // 处理发送消息（可选传入预设问题文本直接发送）
  const handleSendMessage = async (presetText?: string) => {
    const text = (presetText ?? chatInput).trim();
    if (!text || isChatLoading) return;
    if (!presetText) setChatInput('');

    const userMessage = text;
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsChatLoading(true);

    try {
      const context = buildChatContext();
      const systemContent = context
        ? (lang === 'zh'
          ? `你是法律合同助手。用户正在针对当前合同提问。请严格基于以下前置知识作答，不要编造。\n\n${context}`
          : `You are a legal contract assistant. The user is asking about the current contract. Answer strictly based on the context below; do not make up information.\n\n${context}`)
        : (lang === 'zh' ? '你是法律合同助手。请基于用户提供的信息作答。' : 'You are a legal contract assistant. Answer based on user input.');

      // 跳过开头的纯预设提示类 assistant 消息，只保留真实的对话轮次
      let idx = 0;
      while (idx < chatMessages.length && chatMessages[idx].role === 'assistant') idx++;
      const conversation = chatMessages.slice(idx).map(msg => ({ role: msg.role, content: msg.content }));

      const messages: Array<{ role: string; content: string }> = [
        { role: 'system', content: systemContent },
        ...conversation,
        { role: 'user', content: userMessage }
      ];

      const API_BASE_URL = "https://api.openai-next.com/v1";
      const API_KEY = (import.meta.env?.VITE_OPENAI_API_KEY as string) || 'sk-bZb5zjBbGSg6xRjJ9e9a6538Bb1b4b8498446aCfE6F3D685';
      
      const response = await fetch(`${API_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          stream: false,
          messages
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      const assistantContent = data.choices[0]?.message?.content || (lang === 'zh' ? '抱歉，无法生成回复。' : 'Sorry, unable to generate a reply.');

      const assistantMessage = {
        role: 'assistant' as const,
        content: assistantContent,
        isTyping: true
      };
      setChatMessages(prev => [...prev, assistantMessage]);
      
      // 打字机效果显示回复
      let currentIndex = 0;
      const fullText = assistantMessage.content;
      const typingInterval = setInterval(() => {
        if (currentIndex < fullText.length) {
          setChatMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              ...assistantMessage,
              content: fullText.substring(0, currentIndex + 1),
              isTyping: true
            };
            return newMessages;
          });
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          setChatMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              ...assistantMessage,
              isTyping: false
            };
            return newMessages;
          });
          setIsChatLoading(false);
        }
      }, 20);
    } catch (error) {
      console.error('Chat API error:', error);
      const errorMessage = {
        role: 'assistant' as const,
        content: lang === 'zh' ? '抱歉，发生了错误。请稍后重试。' : 'Sorry, an error occurred. Please try again later.',
        isTyping: false
      };
      setChatMessages(prev => [...prev, errorMessage]);
      setIsChatLoading(false);
    }
  };

  // 处理返回按钮
  const handleBackToProgress = () => {
    setIsFadingIn(false);
    setTimeout(() => {
      setCurrentPage('progress');
      setShowChatGPTDialog(false);
      setIsFadingIn(true);
    }, 250);
  };

  // 处理从进度页跳转到对话页
  const handleGoToChat = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      setCurrentPage('chat');
      setShowChatGPTDialog(true);
      setIsFadingOut(false);
      // 先设置为 true（隐藏），然后淡入显示
      setIsFadingIn(true);
      setTimeout(() => {
        setIsFadingIn(false);
      }, 50);
    }, 500);
  };

  // 自动调整textarea高度
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [chatInput]);

  const simulatedSteps = [t('reveal.stepParsing'), t('reveal.stepClauses'), t('reveal.stepContext'), t('reveal.stepReady')];
  const progress = skipProgressAnimation ? 100 : animatedProgress;

  const categoryCards = [
    { key: 'critical', title: t('risk.critical'), count: categoryCounts.critical, color: 'text-red-700', bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)' },
    { key: 'high', title: t('risk.high'), count: categoryCounts.high, color: 'text-orange-700', bg: 'rgba(249, 115, 22, 0.15)', border: 'rgba(249, 115, 22, 0.3)' },
    { key: 'medium', title: t('risk.medium'), count: categoryCounts.medium, color: 'text-yellow-700', bg: 'rgba(234, 179, 8, 0.15)', border: 'rgba(234, 179, 8, 0.3)' },
    { key: 'low', title: t('risk.low'), count: categoryCounts.low, color: 'text-green-700', bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.3)' }
  ];

  // 获取文件名（不带后缀）
  const getFileName = () => {
    if (!activeContract?.name) return '';
    return activeContract.name.replace(/\.[^/.]+$/, '');
  };

  // 如果没有活动合同，显示提示消息
  if (!activeContract) {
    return (
      <div className="h-full w-full flex items-center justify-center p-8">
        <div className="w-full max-w-lg">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl py-14 px-8 shadow-xl flex flex-col items-center text-center border border-white/50">
            {/* 文档图标 */}
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}>
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {lang === 'zh' ? '请先上传文件' : 'Please Upload File First'}
            </h2>
            <p className="text-sm text-slate-500 mb-8">
              {lang === 'zh' ? '在工作台标签页上传合同文件后，即可在此查看分析结果' : 'Upload a contract file in the Workbench tab to view analysis results here'}
            </p>
            {/* 占位元素，用于匹配 AppLayout 中按钮的高度 */}
            <div className="h-[48px]"></div>
          </div>
        </div>
      </div>
    );
  }

  // 统一的容器，包含进度页面和对话页面
  return (
    <div className="h-full w-full flex flex-col overflow-hidden" style={{ paddingTop: '80px', paddingBottom: '24px', paddingLeft: '32px', paddingRight: '32px' }}>
      {/* 液态玻璃容器 - 同一个容器，不淡入淡出 */}
      <div
        className="h-full w-full rounded-3xl flex flex-col overflow-hidden relative"
        style={{
          background: 'rgba(235, 245, 255, 0.18)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '1px solid rgba(220, 235, 250, 0.4)',
          boxShadow: '0 8px 32px rgba(100, 150, 200, 0.08), 0 2px 8px rgba(80, 120, 180, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.5)'
        }}
      >
        {/* 嵌入式液态玻璃进度条 - 完美贴合容器顶部，只在进度页面显示 */}
        {currentPage === 'progress' && (
        <div
          className="relative w-full overflow-hidden"
          style={{
            height: '6%',
            minHeight: '32px',
            maxHeight: '48px',
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px'
          }}
        >
          {/* 进度条容器 - 嵌入效果，贴合边缘 */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(20px) saturate(150%)',
              WebkitBackdropFilter: 'blur(20px) saturate(150%)',
              boxShadow: `
                inset 0 2px 8px rgba(0, 0, 0, 0.15),
                inset 0 -1px 4px rgba(255, 255, 255, 0.1),
                0 1px 0 rgba(255, 255, 255, 0.2)
              `,
              borderTop: '0.5px solid rgba(255, 255, 255, 0.15)'
            }}
          >
            {/* 环境光反射层 - 顶部高光 */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.1) 20%, transparent 50%)',
                animation: progress >= 100 ? 'none' : 'shimmer 3s ease-in-out infinite'
              }}
            />

            {/* 填充进度层 */}
            <div
              className="absolute left-0 top-0 bottom-0 overflow-hidden"
              style={{
                width: `${progress}%`,
                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                willChange: 'width',
                borderTopLeftRadius: '24px'
              }}
            >
              {/* 渐变填充 */}
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(90deg, rgba(255, 255, 255, 0.95) 0%, rgba(230, 240, 250, 0.9) 30%, rgba(215, 230, 245, 0.85) 60%, rgba(200, 220, 240, 0.8) 100%)',
                  boxShadow: `
                    0 0 20px rgba(200, 215, 230, 0.3),
                    inset 0 1px 0 rgba(255, 255, 255, 0.7),
                    inset 0 -1px 0 rgba(180, 195, 210, 0.2)
                  `
                }}
              >
                {/* 液体流动遮罩动画 */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: `
                      linear-gradient(
                        90deg,
                        transparent 0%,
                        rgba(255, 255, 255, 0.3) 25%,
                        rgba(255, 255, 255, 0.5) 50%,
                        rgba(255, 255, 255, 0.3) 75%,
                        transparent 100%
                      )
                    `,
                    backgroundSize: '200% 100%',
                    animation: progress >= 100 ? 'none' : 'liquidFlow 2s ease-in-out infinite',
                    mixBlendMode: 'overlay'
                  }}
                />

                {/* 边缘柔光 */}
                <div
                  className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, transparent, rgba(200, 215, 230, 0.5))',
                    filter: 'blur(8px)'
                  }}
                />
              </div>

              {/* 顶部高光线 */}
              <div
                className="absolute top-0 left-0 right-0 h-[1px] pointer-events-none"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8) 50%, transparent)',
                  boxShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
                }}
              />
            </div>

            {/* 未填充区域的微光效果 */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `
                  radial-gradient(ellipse at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                  radial-gradient(ellipse at 80% 50%, rgba(200, 230, 255, 0.08) 0%, transparent 50%)
                `,
                animation: progress >= 100 ? 'none' : 'ambientGlow 4s ease-in-out infinite alternate'
              }}
            />
          </div>
        </div>
        )}

        {/* 右下角进入对话页面按钮 */}
        {currentPage === 'progress' && (
          <button
            onClick={handleGoToChat}
            className="absolute bottom-6 right-6 w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            style={{
              background: 'rgba(255, 255, 255, 0.15)',
              backdropFilter: 'blur(20px) saturate(150%)',
              WebkitBackdropFilter: 'blur(20px) saturate(150%)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: `
                0 4px 16px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.4)
              `
            }}
            title={lang === 'zh' ? '进入对话' : 'Go to Chat'}
          >
            <svg className="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </button>
        )}

        {/* 主内容区域 */}
        <div className="flex-1 p-8 flex flex-col overflow-hidden">
        {/* 对话页面内容 */}
        {currentPage === 'chat' && showChatGPTDialog ? (
          <div className={`h-full w-full flex flex-col transition-opacity duration-500 ${!isFadingIn ? 'opacity-100' : 'opacity-0'}`} key="chat">
            {/* 标题栏 */}
            <div className="relative flex items-center justify-center mb-6">
              <button
                onClick={handleBackToProgress}
                className="absolute left-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-xl font-semibold text-slate-800 text-center">
                {getFileName()}{lang === 'zh' ? '的对话框' : ' Dialog'}
              </h2>
            </div>

            {/* 消息列表 */}
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto mb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="max-w-3xl mx-auto space-y-6">
                {chatMessages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-3xl px-4 py-3 ${
                      msg.role === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-slate-200 text-slate-800'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.isTyping && <span className="inline-block w-2 h-4 bg-current animate-pulse ml-1 align-middle" />}
          </div>
                  </div>
                ))}
                {/* 对方正在输入中（仅等待 API 时显示，开始打字后消失） */}
                {isChatLoading && chatMessages.length > 0 && chatMessages[chatMessages.length - 1]?.role === 'user' && (
                  <div className="flex justify-start">
                    <div className="rounded-3xl px-4 py-3 bg-slate-200 text-slate-800 max-w-[80%]">
                      <div className="flex items-center gap-1">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                      </div>
                    </div>
                  </div>
                )}
                </div>
            </div>

            {/* 预设问题 */}
            <div className="flex flex-wrap gap-2 mb-3" style={{ paddingLeft: '32px', paddingRight: '32px' }}>
              {[t('reveal.presetQ1'), t('reveal.presetQ2'), t('reveal.presetQ3'), t('reveal.presetQ4')].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => handleSendMessage(q)}
                  disabled={isChatLoading}
                  className="px-3 py-1.5 rounded-2xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid rgba(55, 65, 81, 0.5)',
                    color: 'rgb(51, 65, 85)'
                  }}
                >
                  {q}
                </button>
              ))}
                    </div>

            {/* 输入框 */}
            <div className="w-full mt-2" style={{ paddingLeft: '32px', paddingRight: '32px' }}>
              <div className="flex items-center gap-2 relative">
                <textarea
                  ref={textareaRef}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder={lang === 'zh' ? '输入您的问题...' : 'Enter your question...'}
                  className="w-full px-4 py-3 pr-14 rounded-2xl focus:outline-none resize-none overflow-y-auto"
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(20px) saturate(150%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(150%)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: `
                      0 2px 8px rgba(0, 0, 0, 0.05),
                      inset 0 1px 0 rgba(255, 255, 255, 0.4)
                    `,
                    minHeight: '52px',
                    maxHeight: '200px'
                  }}
                  rows={1}
                      />
                      <button
                  onClick={() => handleSendMessage()}
                  disabled={!chatInput.trim() || isChatLoading}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-slate-300 text-slate-700 hover:bg-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 flex items-center justify-center"
                  title={lang === 'zh' ? '发送' : 'Send'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                      </button>
                    </div>
            </div>
          </div>
        ) : (
          /* 进度页面内容 */
          <div className={`h-full w-full flex flex-col transition-opacity duration-500 ${isFadingIn ? 'opacity-100' : 'opacity-0'}`} key="progress">
            {/* Risk Summary - 一行显示 */}
            <div className="w-full grid grid-cols-4 gap-4 mb-8">
              {categoryCards.map(card => (
                <div key={card.key} className="p-6 rounded-2xl border transition-all duration-500" style={{ backgroundColor: card.bg, borderColor: card.border }}>
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-bold uppercase tracking-wider ${card.color}`}>{card.title}</span>
                    <span className={`text-3xl font-black ${card.color}`}>{animatedCounts[card.key as RiskCategory]}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Thought Chain */}
            <div className="w-full flex flex-col overflow-hidden flex-1">
              <div 
                ref={thoughtChainScrollRef}
                className={`flex-1 p-8 font-mono text-sm leading-relaxed text-slate-700 whitespace-pre-wrap overflow-y-auto transition-opacity duration-500 thought-chain-scroll ${
                  isFadingOut ? 'opacity-0' : 'opacity-100'
                }`}
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {displayedThoughtChain || (analysisState.isAnalyzing ? <span className="animate-pulse">{t('reveal.stepContext')}...</span> : t('reveal.scanning'))}
                
                {/* 法律法规显示 - 在打字机内容下方，同一滚动容器 */}
                {activeContract?.legalReferences && activeContract.legalReferences.length > 0 && isTypingComplete && (
                  <div className="mt-12 font-sans">
                    <h3 className="text-xl font-bold text-slate-800 mb-6">
                      {lang === 'zh' ? '相关法律法规' : 'Related Laws & Regulations'}
                    </h3>
                    <div className="space-y-3">
                      {activeContract.legalReferences.map((ref, idx) => (
                        <div 
                          key={idx} 
                          className="group relative p-5 rounded-2xl transition-all duration-300 hover:shadow-xl overflow-hidden" 
                          style={{ 
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.12) 50%, rgba(59, 130, 246, 0.15) 100%)',
                            backdropFilter: 'blur(20px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderLeft: '4px solid #3b82f6',
                            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(59, 130, 246, 0.2)'
                          }}
                        >
                          {/* 液态玻璃光泽效果 */}
                          <div 
                            className="absolute inset-0 pointer-events-none opacity-30"
                            style={{
                              background: 'linear-gradient(135deg, transparent 0%, rgba(255, 255, 255, 0.3) 25%, rgba(255, 255, 255, 0.5) 50%, rgba(255, 255, 255, 0.3) 75%, transparent 100%)',
                              backgroundSize: '200% 200%',
                              animation: 'liquidShimmer 3s ease-in-out infinite',
                              mixBlendMode: 'overlay'
                            }}
                          />
                          {/* 顶部高光 */}
                          <div 
                            className="absolute top-0 left-0 right-0 h-px pointer-events-none"
                            style={{
                              background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6) 50%, transparent)',
                              boxShadow: '0 1px 2px rgba(255, 255, 255, 0.5)'
                            }}
                          />
                          
                          <div className="relative flex items-start gap-3">
                            <div className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center mt-0.5" style={{
                              background: 'rgba(59, 130, 246, 0.25)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              color: '#2563eb',
                              border: '1px solid rgba(59, 130, 246, 0.4)',
                              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                            }}>
                              <span className="text-xs font-bold">{idx + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-800 mb-2 text-base leading-snug">
                                {ref.title}
                                {ref.article && (() => {
                                  // 清理 article 字段，移除可能已存在的"第"和"条"
                                  let cleanArticle = ref.article.trim();
                                  // 移除开头的"第"
                                  cleanArticle = cleanArticle.replace(/^第+/, '');
                                  // 移除结尾的"条"
                                  cleanArticle = cleanArticle.replace(/条+$/, '');
                                  return (
                                    <span className="ml-2 px-2 py-0.5 rounded-md text-sm font-medium" style={{
                                      background: 'rgba(59, 130, 246, 0.2)',
                                      backdropFilter: 'blur(10px)',
                                      WebkitBackdropFilter: 'blur(10px)',
                                      color: '#2563eb',
                                      border: '1px solid rgba(59, 130, 246, 0.3)',
                                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                                    }}>
                                      第{cleanArticle}条
                                    </span>
                                  );
                                })()}
                              </div>
                              <div className="text-sm text-slate-600 leading-relaxed mb-3" style={{ lineHeight: '1.7' }}>
                                {ref.content}
                              </div>
                              {ref.url && (
                                <a 
                                  href={ref.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  {lang === 'zh' ? '查看原文' : 'View Source'}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      <style>{`
        div::-webkit-scrollbar { display: none; }
        .thought-chain-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .thought-chain-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes fade-in-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fade-in-up 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
        @keyframes liquidShimmer {
          0%, 100% { background-position: -200% 0; }
          50% { background-position: 200% 0; }
        }
        @keyframes shimmer {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.6;
          }
        }
        @keyframes liquidFlow {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        @keyframes ambientGlow {
          0% {
            opacity: 0.4;
          }
          100% {
            opacity: 0.8;
          }
        }
        @keyframes typingBounce {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.5;
          }
          30% {
            transform: translateY(-6px);
            opacity: 1;
          }
        }
        .typing-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          animation: typingBounce 1.4s ease-in-out infinite;
        }
        .typing-dot:nth-child(2) { animation-delay: 0.2s; }
        .typing-dot:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
    </div>
  );
};
